import { CompactPagination } from "@/components/Pagination";
import { ActionTitle } from "@/components/actionTitle/ActionTitle";
import {
  ScrollContainer,
  WidgetContainer,
} from "@/features/workspace/widgets/Components";
import {
  type InstallationQuotationWidgetList,
  type WidgetSetting,
  type WidgetSize,
  useInstallationQuotationSearch,
} from "@repo/api";
import { Text } from "@repo/skapa";
import { t } from "@repo/translation";
import { useEffect, useMemo, useState } from "react";

import { CARD_HEIGHT_NORMAL, CARD_HEIGHT_TALL } from "../constants";
import { InstallationQuotationList } from "./InstallationQuotationList";
import { getWidgetSettings, mapInstallationQuotationData } from "./utils";

interface InstallationQuotationsWidgetProps {
  title: string;
  settings?: WidgetSetting;
  widgetSize: WidgetSize;
}

export const InstallationQuotationWidget = ({
  title,
  settings,
  widgetSize,
}: InstallationQuotationsWidgetProps) => {
  const [installationQuotations, setInstallationQuotations] = useState<
    InstallationQuotationWidgetList[]
  >([]);
  const [pageIndex, setPageIndex] = useState(0);

  const pageSize = useMemo(() => (widgetSize === "M" ? 10 : 5), [widgetSize]);

  const widgetSettings = useMemo(
    () => ({
      ...getWidgetSettings(settings),
      pageSize,
      pageIndex,
    }),
    [settings, pageSize, pageIndex]
  );

  const { data, isValidating } = useInstallationQuotationSearch(widgetSettings);

  useEffect(() => {
    if (data?.content) {
      setInstallationQuotations((prevInstallationQuotations) =>
        mapInstallationQuotationData(prevInstallationQuotations, data.content)
      );
    }
  }, [data]);

  const loadMoreQuotations = () => {
    setPageIndex((prev) => prev + 1);
  };

  const totalItems = data?.totalElements ?? 0;

  const widgetHeight =
    widgetSize === "M" ? CARD_HEIGHT_TALL : CARD_HEIGHT_NORMAL;

  return (
    <WidgetContainer size={widgetHeight}>
      <ActionTitle title={`${title} (${installationQuotations.length})`} />

      {!isValidating && installationQuotations.length === 0 ? (
        <Text>{t("prompt.no_installation_quotations")}</Text>
      ) : (
        <>
          <ScrollContainer>
            <InstallationQuotationList
              installationQuotations={installationQuotations}
            />
          </ScrollContainer>

          {installationQuotations.length < totalItems && (
            <CompactPagination
              setSize={loadMoreQuotations}
              size={pageSize}
              totalNumber={totalItems}
              currentNumber={installationQuotations.length}
            />
          )}
        </>
      )}
    </WidgetContainer>
  );
};
